<template>
  <div class="container mt-3">
    
    <SectionHeader title="report filters">
      <button type="button" class="btn btn-sm btn-primary ms-2" data-tippy-content="reset" @click="reset()" >
        <font-awesome-icon icon="trash-restore" />
      </button>
    </SectionHeader>

    <VeeForm v-slot="{ invalid }">
      <form autocomplete="off">

        <div class="form-group">
          <label for="selectedDate">select date range</label>
          <VueDatePicker
            v-model="selectedDate" 
            range 
            :preset-dates="dpCustomShortcuts" 
            :max-date="dpMaxDate"
            format="MMM dd, yyyy"
            placeholder="Select date & time"
            :enable-time-picker="false"
            :partial-range="false"
            :action-row="{ showSelect: true, showCancel: false, showNow: false, showPreview: true }"
            teleport="body"
            :teleport-center="false"
            />
        </div>

        <div class="form-group">
          <label>search list</label>
          <Field name="searchList" rules="searchTermCsv" v-slot="{ field, errors }">
            <input
              type="text" 
              class="form-control" 
              name="searchList" 
              v-bind="field"
              v-model="searchList"
              placeholder="i.e. kent,004A61AC"
            >
            <span class="tip">comma separeted list of search terms among: lrrId, commercialName, supplierId, lrr name</span>
            <p class="error">{{ errors[0] }}</p>
          </Field>
        </div>

        <div class="form-group">
          <label>exclude list</label>
          <Field name="excludeList" rules="searchTermCsv" v-slot="{ field, errors }">
            <input
              type="text" 
              class="form-control" 
              name="excludeList" 
              v-bind="field"
              v-model="excludeList"
              placeholder="i.e. techtellic"
            >
            <span class="tip">comma separeted list of exclude terms: lrrId, commercialName, supplierId, lrr name</span>
            <p class="error">{{ errors[0] }}</p>
          </Field>
        </div>

        <div class="form-group">
          <label for="minOutageSec">min outage filter (seconds)</label>
          <Field name="minOutageSec" rules="required|min_value:60" v-slot="{ field, errors }">
            <input type="number" class="form-control" step="10" name="minOutageSec" placeholder="i.e. 60" v-bind="field" v-model="minOutageSec">  
            <span class="tip">the minimum outage length (in seconds) to be included in the report</span>
            <p class="error">{{ errors[0] }}</p>
          </Field>
        </div>

        <button 
          class="btn btn-info"
          type="submit" 
          @click.prevent="generateReport()" 
          :disabled="invalid || !canGenerateReport"
>
          <font-awesome-icon icon="redo-alt"/> generate report
        </button>
      </form>

    </VeeForm>

    <Loading v-if="httpBusy"/>
    <Error :label="error"/>

    <GeneratedReport :report="currentReport" v-if="canShowReport" />
    
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { ApiClient, curApiClient } from '../services/ApiClient';
import { NetworkUptimeReport } from '@/types';
import Error from '@/components/Error.vue';
import Loading from '@/components/Loading.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import GeneratedReport from '@/components/reports/GeneratedReport.vue'; 

import dayjs from '@/plugins/dayjs';

const httpBusy = ref(false);
const error = ref('');

// model
const selectedDate = ref<Date[]>([]);
const searchList = ref('');
const excludeList = ref('');
const minOutageSec = ref(60);

const currentReport = ref<NetworkUptimeReport>({} as NetworkUptimeReport);

// services
const apiClient: ApiClient = curApiClient;

onMounted(() => {
  // todo
});

async function generateReport() {
  const now = new Date().getTime();

  const report = {
    startTs: selectedDate.value[0] ? selectedDate.value[0].getTime() : 0,
    endTs: selectedDate.value[1] ? selectedDate.value[1].getTime() : now,
    searchList: searchList.value.replace(/\s+/g, ''),
    excludeList: excludeList.value.replace(/\s+/g, ''),
    minOutageSec: minOutageSec.value
  };

  // we correct with max endTs = now
  if (report.endTs > now) {
    report.endTs = now - 60000;
  }

  console.log(`Generating report ${JSON.stringify(report)}`);

  httpBusy.value = true;
  error.value = '';
  const reply = await apiClient.getUptimeReport(report);
  httpBusy.value = false;
  
  if (reply.errorMsg) {
    return error.value = `Unable to generate report because ${reply.errorMsg}`;
  }

  currentReport.value = reply.report!;
}

const dpCustomShortcuts = computed(() => {
  const now = new Date();
  return [
    { label: 'This week', value: [dayjs().startOf('isoWeek').toDate(), now] },
    { label: 'Last week', value: [dayjs().subtract(1, 'week').startOf('isoWeek').toDate(), dayjs().subtract(1, 'week').endOf('isoWeek').toDate()] },
    { label: 'Last 7 days', value: [dayjs().subtract(7, 'days').toDate(), now] },
    { label: 'Last 30 days', value: [dayjs().subtract(30, 'days').toDate(), now] },
    { label: 'This month', value: [dayjs().startOf('month').toDate(), now] },
    { label: 'Last month', value: [dayjs().subtract(1, 'month').startOf('month').toDate(), dayjs().subtract(1, 'month').endOf('month').toDate()] }
  ];
});

const dpMaxDate = computed((): Date => {
  return new Date();
});

const canGenerateReport = computed((): boolean => {
  return !httpBusy.value && selectedDate.value.length === 2 && !!selectedDate.value[0] && !!selectedDate.value[1];
});

const canShowReport = computed((): boolean => {
  return !httpBusy.value && currentReport.value.uptimePerc !== undefined;
});

function reset() {
  selectedDate.value = [];
  searchList.value = '';
  excludeList.value = '';
  minOutageSec.value = 60;
  currentReport.value = {} as NetworkUptimeReport;
}
</script>


<style scoped lang="scss">

.search-container {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
}

.search-container-phone {
  width: 90%;
  margin-right: auto;
  margin-left: auto;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
}

// Date picker styling to match production
:deep(.dp__theme_light) {
  --dp-background-color: #ffffff;
  --dp-text-color: #212529;
  --dp-hover-color: #f8f9fa;
  --dp-hover-text-color: #212529;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #5D9CCA;
  --dp-primary-text-color: #ffffff;
  --dp-secondary-color: #c0c4cc;
  --dp-border-color: #ddd;
  --dp-menu-border-color: #ddd;
  --dp-border-color-hover: #aaaeb7;
  --dp-disabled-color: #f6f6f6;
  --dp-disabled-color-text: #8e8e8e;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-highlight-color: rgba(93, 156, 202, 0.3);
  --dp-range-between-dates-background-color: rgba(93, 156, 202, 0.2);
  --dp-range-between-dates-text-color: #212529;
}

:deep(.dp__input) {
  background-color: #ffffff !important;
  color: #212529 !important;
  border: 1px solid #ced4da !important;
}

:deep(.dp__input::placeholder) {
  color: #6c757d !important;
}

// Make selected/highlighted dates more prominent
:deep(.dp__today) {
  background-color: #5D9CCA !important;
  color: #ffffff !important;
  font-weight: bold !important;
}

:deep(.dp__active_date) {
  background-color: #5D9CCA !important;
  color: #ffffff !important;
  font-weight: bold !important;
}

:deep(.dp__range_start),
:deep(.dp__range_end) {
  background-color: #5D9CCA !important;
  color: #ffffff !important;
  font-weight: bold !important;
}

:deep(.dp__range_between) {
  background-color: rgba(93, 156, 202, 0.3) !important;
  color: #212529 !important;
}

// Force left alignment instead of center
:deep(.dp__menu) {
  position: absolute !important;
  left: 0 !important;
  right: auto !important;
  transform: none !important;
  margin-left: 0 !important;
}

// Position relative to input field
:deep(.dp__main) {
  position: relative !important;
}

// Style the preset sidebar exactly like production
:deep(.dp__preset_dates) {
  background-color: #6BAED6 !important;
  color: white !important;
  width: 140px !important;
  border-radius: 6px 0 0 6px !important;
}

:deep(.dp__preset_date) {
  color: #5D9CCA !important;
  background-color: transparent !important;
  padding: 8px 12px !important;
  border: none !important;
  cursor: pointer !important;
  font-size: 13px !important;
  text-align: left !important;
}

:deep(.dp__preset_date:hover) {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
}

// Action row to show selected range at top
:deep(.dp__action_row) {
  background-color: #6BAED6 !important;
  color: white !important;
  padding: 8px 16px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-align: center !important;
  order: -1 !important;
}

:deep(.dp__selection_preview) {
  color: white !important;
  font-weight: 600 !important;
}

// Month/year header styling  
:deep(.dp__month_year_wrap) {
  background-color: #6BAED6 !important;
  color: white !important;
  padding: 8px 16px !important;
  margin: 0 !important;
  border-radius: 0 !important;
  text-align: center !important;
}

:deep(.dp__month_year_select) {
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
}

:deep(.dp__arrow_top) {
  color: white !important;
  background: none !important;
  border: none !important;
}

// Calendar styling
:deep(.dp__instance_calendar) {
  border: 1px solid #ddd !important;
  border-radius: 0 6px 6px 0 !important;
  background-color: white !important;
}

:deep(.dp__calendar_header) {
  background-color: #f8f9fa !important;
  padding: 6px !important;
  border-bottom: 1px solid #ddd !important;
}

:deep(.dp__calendar_header_item) {
  color: #666 !important;
  font-weight: 500 !important;
  font-size: 11px !important;
  text-align: center !important;
}

// Flex container to ensure proper layout
:deep(.dp__menu_inner) {
  display: flex !important;
  flex-direction: column !important;
}

</style>